import firstBy from 'thenby'
import { getTaskTypePriorityOfProd } from '@/lib/productions'

export const sortAssets = assets => {
  return assets.sort(
    firstBy('canceled')
      .thenBy((a, b) => a.asset_type_name.localeCompare(b.asset_type_name))
      .thenBy((a, b) => a.name.localeCompare(b.name))
  )
}

export const sortShots = shots => {
  return shots.sort(
    firstBy('canceled')
      .thenBy(sortByEpisode)
      .thenBy((a, b) => a.sequence_name.localeCompare(b.sequence_name))
      .thenBy((a, b) => a.name.localeCompare(b.name))
  )
}

export const sortEdits = shots => {
  return shots.sort(
    firstBy('canceled')
      .thenBy(sortByEpisode)
      .thenBy((a, b) => a.name.localeCompare(b.name))
  )
}

export const sortSequences = sequences => {
  return sequences.sort(
    firstBy((a, b) => {
      if (a.episode_name) {
        return a.episode_name.localeCompare(b.episode_name)
      } else {
        return 0
      }
    }).thenBy((a, b) => {
      return a.name.localeCompare(b.name)
    })
  )
}

export const sortProductions = productions => {
  return productions.sort((a, b) => {
    if (a.project_status_name === b.project_status_name) {
      if (!a.number) { return -1 }
      else if (!b.number) { return 1 }
      return a.number.localeCompare(b.number)
    } else {
      return -1 * a.project_status_name.localeCompare(b.project_status_name)
    }
  })
}

export const sortTaskNames = (tasks, taskTypeMap) => {
  return tasks.sort(
    firstBy((a, b) => {
      const taskTypeA = taskTypeMap.get(a.task_type_id)
      const taskTypeB = taskTypeMap.get(b.task_type_id)
      return taskTypeA.name.localeCompare(taskTypeB.name)
    }).thenBy((a, b) => {
      if (a.full_entity_name) {
        return a.full_entity_name.localeCompare(b.full_entity_name)
      } else {
        return a.entity_name.localeCompare(b.entity_name)
      }
    })
  )
}
export const sortTasks = (tasks, taskTypeMap) => {
  return tasks.sort(
    firstBy('priority', -1)
      .thenBy((a, b) => {
        if (a.project_name) {
          return a.project_name.localeCompare(b.project_name)
        } else {
          return 0
        }
      })
      .thenBy((a, b) => {
        const taskTypeA = taskTypeMap.get(a.task_type_id)
        const taskTypeB = taskTypeMap.get(b.task_type_id)
        return taskTypeA.name.localeCompare(taskTypeB.name)
      })
      .thenBy((a, b) => {
        if (a.full_entity_name) {
          return a.full_entity_name.localeCompare(b.full_entity_name)
        } else {
          return a.entity_name.localeCompare(b.entity_name)
        }
      })
  )
}

export const sortComments = (comments) => {
  return comments.sort(
    firstBy('pinned', -1)
      .thenBy('created_at', -1)
  )
}

export const sortRevisionPreviewFiles = (previewFiles) => {
  return previewFiles.sort(
    firstBy('position')
      .thenBy('created_at')
  )
}

export const sortTaskTypes = (taskTypes, currentProduction) => {
  return taskTypes.sort(
    firstBy('for_entity')
      .thenBy((taskTypeA, taskTypeB) => {
        const taskTypeAPriority = getTaskTypePriorityOfProd(
          taskTypeA,
          currentProduction
        )
        const taskTypeBPriority = getTaskTypePriorityOfProd(
          taskTypeB,
          currentProduction
        )
        if (taskTypeAPriority > taskTypeBPriority) {
          return 1
        } else if (taskTypeAPriority < taskTypeBPriority) {
          return -1
        }
        return 0
      })
      .thenBy('name')
  )
}

export const sortTaskTypeScheduleItems = (
  items,
  currentProduction,
  taskTypeMap
) => {
  const sortFunc = firstBy('for_entity')
    .thenBy((itemA, itemB) => {
      const taskTypeA = taskTypeMap.get(itemA.task_type_id)
      const taskTypeB = taskTypeMap.get(itemB.task_type_id)
      const taskTypeAPriority = getTaskTypePriorityOfProd(
        taskTypeA,
        currentProduction
      )
      const taskTypeBPriority = getTaskTypePriorityOfProd(
        taskTypeB,
        currentProduction
      )
      if (taskTypeAPriority > taskTypeBPriority) {
        return 1
      } else if (taskTypeAPriority < taskTypeBPriority) {
        return -1
      }
      return 0
    })
    .thenBy('name')
  return items.sort(sortFunc)
}

export const groupTaskTypeScheduleItems = (
  items,
  taskTypeMap,
  departmentMap
) => {
  const res = [
    {
      id: 'shots',
      name: 'Shots',
      expanded: false,
      editable: false,
      color: '#FF853B',
      children: []
    },
    {
      id: 'assets',
      name: 'Assets',
      expanded: false,
      editable: false,
      color: '#9C8D39',
      children: []
    },
    {
      id: 'edits',
      name: 'Edits',
      expanded: false,
      editable: false,
      color: '#9D3BFF',
      children: []
    },
    {
      id: 'management',
      name: 'Management',
      expanded: false,
      editable: false,
      color: '#FF3B3B',
      children: []
    }
  ]
  for (const item of items) {
    item.editable = true
    item.color = '#4f9bb9'
    var resInd
    switch (item.for_entity) {
      case 'Shot':
        resInd = 0
        break
      case 'Asset':
        resInd = 1
        break
      case 'Edit':
        resInd = 2
        break
      case 'Management':
        resInd = 3
        item.lowest = true
        break
      default:
        return
    }
    const taskType = taskTypeMap.get(item.task_type_id)
    if (taskType) {
      const deptId = taskType.department_id
      var deptIndex = res[resInd].children.findIndex((department) => {
        return department.id === deptId
      })
      if (deptIndex < 0) {
        res[resInd].children.push({
          name: departmentMap.get(deptId).name,
          color: '#7ac45c',
          expanded: false,
          id: deptId,
          for_entity: item.for_entity,
          children: [],
          parentElement: res[resInd],
          editable: false
        })
        deptIndex = res[resInd].children.length - 1
      }
      // item.editable = false
      item.parentElement = res[resInd].children[deptIndex]
      res[resInd].children[deptIndex].children.push(item)
    }
  }

  res.forEach((elem1) => {
    if (elem1.children) {
      elem1.children.forEach((elem2) => {
        if (elem2.children) {
          elem2.children = elem2.children.sort(firstBy('priority'))
        }
      })
      elem1.children = elem1.children.sort(firstBy('priority'))
    }
  })

  return res
}

export const sortPlaylists = (playlists) => {
  return playlists.sort(
    firstBy('created_at', -1).thenBy((a, b) => a.name.localeCompare(b.name))
  )
}

export const sortPeople = people => {
  return people.sort(
    firstBy('active', -1)
      .thenBy((a, b) =>
        a.first_name ? a.first_name.localeCompare(b.first_name) : -1
      )
      .thenBy((a, b) =>
        a.last_name ? a.last_name.localeCompare(b.last_name) : -1
      )
  )
}

export const sortPeopleBy = (people, key, dir = -1) => {
  if (key === 'name') {
    return people.sort(
      firstBy('first_name', dir)
      .thenBy((a, b) =>
        a.last_name ? a.last_name.localeCompare(b.last_name) : dir
      )
    )
  } else {
    return people.sort(
      firstBy(key, dir)
      .thenBy((a, b) =>
        a.first_name ? a.first_name.localeCompare(b.first_name) : dir
      )
    )
  }
}

export const sortByName = entries => {
  return entries.sort((a, b) => a.name.localeCompare(b.name))
}

export const sortByDate = entries => {
  return entries.sort(firstBy('created_at', -1))
}

export const sortByPlainDate = entries => {
  return entries.sort(firstBy('date', -1))
}

export const sortValidationColumns = (
  columns,
  taskTypeMap,
  currentProduction
) => {
  return columns.sort((a, b) => {
    const taskTypeA = taskTypeMap.get(a)
    const taskTypeB = taskTypeMap.get(b)
    const taskTypeAPriority = getTaskTypePriorityOfProd(
      taskTypeA,
      currentProduction
    )
    const taskTypeBPriority = getTaskTypePriorityOfProd(
      taskTypeB,
      currentProduction
    )
    if (taskTypeAPriority === taskTypeBPriority) {
      return taskTypeA.name.localeCompare(taskTypeB.name)
    } else if (taskTypeAPriority > taskTypeBPriority) {
      return 1
    } else {
      return -1
    }
  })
}

export const sortColumnsByDepartment = (
  columns,
  taskTypeMap,
  departmentMap,
  currentProduction
) => {
  return columns.sort((a, b) => {
    const taskTypeA = taskTypeMap.get(a)
    const taskTypeB = taskTypeMap.get(b)
    const departmentA = departmentMap.get(taskTypeA.department_id)
    const departmentB = departmentMap.get(taskTypeB.department_id)
    const taskTypeAPriority = getTaskTypePriorityOfProd(
      taskTypeA, currentProduction
    )
    const taskTypeBPriority = getTaskTypePriorityOfProd(
      taskTypeB, currentProduction
    )
    if (departmentA && departmentB && departmentA.id !== departmentB.id) {
      return departmentA.name.localeCompare(departmentB.name)
    } else {
      if (taskTypeAPriority === taskTypeBPriority) {
        return taskTypeB.name.localeCompare(taskTypeA.name)
      } else if (taskTypeAPriority > taskTypeBPriority) {
        return 1
      } else {
        return -1
      }
    }
  })
}

export const sortScheduleItems = (scheduleItems, taskTypeMap) => {
  return scheduleItems.sort(
    firstBy('for_shots')
      .thenBy('priority')
      .thenBy('name')
      .thenBy('start_date')
  )
}

export const sortAssetResult = (
  result,
  sorting,
  taskTypeMap,
  taskMap
) => {
  if (sorting && sorting.length > 0) {
    const sortInfo = sorting[0]
    let sortEntities = sortByTaskType(taskMap, sortInfo)
    if (sortInfo.type === 'metadata') sortEntities = sortByMetadata(sortInfo)
    result = result.sort(
      firstBy('canceled')
        .thenBy(sortEntities)
        .thenBy((a, b) => a.asset_type_name.localeCompare(b.asset_type_name))
        .thenBy((a, b) => a.name.localeCompare(b.name))
    )
  } else {
    result = sortAssets(result)
  }
  return result
}

export const sortShotResult = (result, sorting, taskTypeMap, taskMap) => {
  if (sorting && sorting.length > 0) {
    const sortInfo = sorting[0]
    let sortEntities = sortByTaskType(taskMap, sortInfo)
    if (sortInfo.type === 'metadata') sortEntities = sortByMetadata(sortInfo)
    result = result.sort(
      firstBy('canceled')
        .thenBy(sortEntities)
        .thenBy(sortByEpisode)
        .thenBy((a, b) => a.sequence_name.localeCompare(b.sequence_name))
        .thenBy((a, b) => a.name.localeCompare(b.name))
    )
  } else {
    result = sortShots(result)
  }
  return result
}

export const sortSequenceResult = (result, sorting, taskTypeMap, taskMap) => {
  if (sorting && sorting.length > 0) {
    const sortInfo = sorting[0]
    let sortEntities = sortByTaskType(taskMap, sortInfo)
    if (sortInfo.type === 'metadata') sortEntities = sortByMetadata(sortInfo)
    result = result.sort(
      firstBy('canceled')
        .thenBy(sortEntities)
        .thenBy(sortByEpisode)
        .thenBy((a, b) => a.name.localeCompare(b.name))
    )
  } else {
    result = sortByName(result)
  }
  return result
}

export const sortEpisodeResult = (result, sorting, taskTypeMap, taskMap) => {
  if (sorting && sorting.length > 0) {
    const sortInfo = sorting[0]
    let sortEntities = sortByTaskType(taskMap, sortInfo)
    if (sortInfo.type === 'metadata') sortEntities = sortByMetadata(sortInfo)
    result = result.sort(
      firstBy('canceled')
        .thenBy(sortEntities)
        .thenBy((a, b) => a.name.localeCompare(b.name))
    )
  } else {
    result = sortByName(result)
  }
  return result
}

export const sortEditResult = (result, sorting, taskTypeMap, taskMap) => {
  if (sorting && sorting.length > 0) {
    const sortInfo = sorting[0]
    let sortEntities = sortByTaskType(taskMap, sortInfo)
    if (sortInfo.type === 'metadata') sortEntities = sortByMetadata(sortInfo)
    result = result.sort(
      firstBy('canceled')
        .thenBy(sortEntities)
        .thenBy(sortByEpisode)
        .thenBy((a, b) => a.name.localeCompare(b.name))
    )
  } else {
    result = sortEdits(result)
  }
  return result
}

const sortByMetadata = sortInfo => (a, b) => {
  const dataA = a.data && a.data[sortInfo.column] ? a.data[sortInfo.column] : ''
  const dataB = b.data && b.data[sortInfo.column] ? b.data[sortInfo.column] : ''
  if (!dataA) return 1
  if (!dataB) return -1
  return dataA.localeCompare(dataB)
}

const sortByTaskType = (taskMap, sortInfo) => (a, b) => {
  const taskA = a.validations.get(sortInfo.column)
  const taskB = b.validations.get(sortInfo.column)
  if (!taskA) return -1
  if (!taskB) return 1
  const taskStatusA = taskMap.get(taskA).task_status_short_name
  const taskStatusB = taskMap.get(taskB).task_status_short_name
  return taskStatusA.localeCompare(taskStatusB)
}

const sortByEpisode = (a, b) => {
  if (a.episode_name) {
    return a.episode_name.localeCompare(b.episode_name)
  } else {
    return 0
  }
}
